import { useNavigate } from 'react-router-dom';
import { Trophy, Target, Clock, RotateCcw, Home, Lightbulb, Star, Unlock } from 'lucide-react';
import type { QuizSession, FlagInfo } from '@/types';
import { Button, Card } from '@/components/common';
import { FlagImage } from '@/components/flag';
import { formatDuration } from '@/lib/utils';
import { cn } from '@/lib/utils';
import { useAchievementsStore, type QuizLength } from '@/store';
import flagInfoData from '@/data/flag-info.json';
import countries from '@/data/countries.json';

const flagInfo = flagInfoData as Record<string, FlagInfo>;
const TOTAL_COUNTRIES = countries.length;
const QUIZ_LENGTHS: QuizLength[] = [20, 50, 100, 'all'];

function getQuizLength(count: number): QuizLength {
  if (count === TOTAL_COUNTRIES) return 'all';
  if (count >= 100) return 100;
  if (count >= 50) return 50;
  return 20;
}

function getNextLength(length: QuizLength): QuizLength | null {
  const index = QUIZ_LENGTHS.indexOf(length);
  if (index < QUIZ_LENGTHS.length - 1) {
    return QUIZ_LENGTHS[index + 1];
  }
  return null;
}

function getLengthLabel(length: QuizLength): string {
  return length === 'all' ? `All ${TOTAL_COUNTRIES}` : String(length);
}

interface QuizSummaryProps {
  session: QuizSession;
  onRestart: () => void;
}

export function QuizSummary({ session, onRestart }: QuizSummaryProps) {
  const navigate = useNavigate();
  const { isLengthUnlocked } = useAchievementsStore();

  const correctCount = session.answers.filter((a) => a.correct).length;
  const totalCount = session.questions.length;
  const accuracy = Math.round((correctCount / totalCount) * 100);
  const duration = session.endTime ? session.endTime - session.startTime : 0;
  const isPerfect = correctCount === totalCount;

  // Determine quiz length and what was unlocked
  const quizLength = getQuizLength(totalCount);
  const nextLength = getNextLength(quizLength);
  const justUnlocked = isPerfect && nextLength && isLengthUnlocked(session.mode, nextLength);

  const getGrade = () => {
    if (isPerfect) return { emoji: '⭐', text: 'Perfect!', color: 'text-amber-500' };
    if (accuracy >= 90) return { emoji: '🏆', text: 'Excellent!', color: 'text-warning-500' };
    if (accuracy >= 70) return { emoji: '🌟', text: 'Great job!', color: 'text-success-500' };
    if (accuracy >= 50) return { emoji: '👍', text: 'Good effort!', color: 'text-primary-500' };
    return { emoji: '💪', text: 'Keep practicing!', color: 'text-accent-purple' };
  };

  const grade = getGrade();

  const incorrectAnswers = session.answers.filter((a) => !a.correct);

  return (
    <div className="space-y-6">
      {/* Result header */}
      <div className="text-center space-y-2">
        <div className="text-6xl">{grade.emoji}</div>
        <h2 className={cn('text-3xl font-bold', grade.color)}>{grade.text}</h2>
        <p className="text-gray-500">You completed the quiz!</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="text-center p-4">
          <Target className="w-6 h-6 mx-auto mb-2 text-success-500" />
          <div className="text-2xl font-bold">{accuracy}%</div>
          <div className="text-xs text-gray-500">Accuracy</div>
        </Card>

        <Card className="text-center p-4">
          <Trophy className="w-6 h-6 mx-auto mb-2 text-warning-500" />
          <div className="text-2xl font-bold">
            {correctCount}/{totalCount}
          </div>
          <div className="text-xs text-gray-500">Correct</div>
        </Card>

        <Card className="text-center p-4">
          <Clock className="w-6 h-6 mx-auto mb-2 text-primary-500" />
          <div className="text-2xl font-bold">{formatDuration(duration)}</div>
          <div className="text-xs text-gray-500">Time</div>
        </Card>
      </div>

      {/* Achievement celebration */}
      {isPerfect && (
        <Card className="p-4 bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-100 dark:bg-amber-800/50 rounded-full">
              <Star className="w-6 h-6 text-amber-500" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-amber-900 dark:text-amber-100">
                Achievement Unlocked!
              </h3>
              <p className="text-sm text-amber-700 dark:text-amber-300">
                Perfect score on {getLengthLabel(quizLength)} questions!
              </p>
            </div>
          </div>
          {justUnlocked && nextLength && (
            <div className="mt-3 pt-3 border-t border-amber-200 dark:border-amber-700 flex items-center gap-2 text-sm text-amber-800 dark:text-amber-200">
              <Unlock className="w-4 h-4" />
              <span>
                <strong>{getLengthLabel(nextLength)}</strong> questions now unlocked!
              </span>
            </div>
          )}
        </Card>
      )}

      {/* Incorrect answers review with tips */}
      {incorrectAnswers.length > 0 && (
        <div className="space-y-4">
          <h3 className="font-semibold">Review & Learn</h3>
          {incorrectAnswers.map((answer) => {
            const question = session.questions.find(
              (q) => q.id === answer.questionId
            );
            if (!question) return null;

            const info = flagInfo[question.targetCountry.code];

            return (
              <Card key={answer.questionId} className="p-4">
                <div className="flex items-center gap-4 mb-3">
                  <FlagImage
                    countryCode={question.targetCountry.code}
                    countryName={question.targetCountry.name}
                    size="md"
                  />
                  <div>
                    <div className="font-semibold">{question.targetCountry.name}</div>
                    <div className="text-sm text-gray-500">
                      {question.targetCountry.region}
                    </div>
                  </div>
                </div>

                {info && (
                  <div className="bg-primary-50 dark:bg-primary-900/20 rounded-lg p-3">
                    <div className="flex items-center gap-2 text-primary-600 dark:text-primary-400 text-sm font-medium mb-1">
                      <Lightbulb className="w-4 h-4" />
                      Memory Tip
                    </div>
                    <p className="text-sm">{info.mnemonic}</p>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-4">
        <Button
          variant="secondary"
          onClick={() => navigate('/')}
          className="flex-1 flex items-center justify-center gap-2"
        >
          <Home className="w-4 h-4" />
          Home
        </Button>
        <Button
          onClick={onRestart}
          className="flex-1 flex items-center justify-center gap-2"
        >
          <RotateCcw className="w-4 h-4" />
          Play Again
        </Button>
      </div>
    </div>
  );
}
